export const processRoutingDe = {
  title: 'Prozess- und Arbeitsplan',
  subtitle:
    'Versionierte Arbeitsanweisungen, Qualitätsprüfungen, Ausbeute und Verluste für exakt diese Rezepturversion.',
  statuses: {
    DRAFT: 'Entwurf',
    IN_REVIEW: 'In Prüfung',
    APPROVED: 'Freigegeben',
    BLOCKED: 'Gesperrt',
    ARCHIVED: 'Archiviert',
  },
  help: {
    open: 'Hilfe zum Arbeitsplan öffnen',
    header:
      'Die stabile Arbeitsplannummer gehört zur Rezeptur; jede Rezepturversion besitzt einen eigenen kontrollierten Arbeitsplanstand.',
    translations:
      'Bezeichnungen und Anweisungen können in allen unterstützten Sprachen gepflegt werden.',
    steps:
      'Prozessschritte per Drag-and-drop ordnen. Dezimalfaktoren werden serverseitig ohne Gleitkommaarithmetik berechnet.',
    history:
      'Jeder Arbeitsplanstand bleibt mit seiner Rezepturversion verbunden und ist nach Einreichung unveränderlich.',
    saveBeforeDocuments:
      'Speichere den Arbeitsplan einmal, bevor Prozessdokumente und Fotos in seine geschützte DMS-Akte geladen werden.',
    snapshotDocuments:
      'Die gewählten geschützten Originale bilden den unveränderlichen Dokument-Snapshot dieses Arbeitsplanstands.',
  },
  actions: {
    reorder: 'Prozessschritt umordnen',
    scaleBatch: 'Batch skalieren',
    submit: 'Zur Prüfung',
    review: 'Prüfung abschließen',
    approve: 'Freigeben',
    copy: 'Neue Arbeitsplanversion',
    block: 'Sperren',
    archive: 'Archivieren',
    addStep: 'Schritt hinzufügen',
    addByproduct: 'Nebenprodukt hinzufügen',
  },
  messages: {
    saved: 'Arbeitsplan gespeichert.',
    invalid: 'Ungültige Arbeitsplandaten oder JSON-Strukturen korrigieren.',
    submitted: 'Arbeitsplan zur unabhängigen Prüfung eingereicht.',
    workflowUpdated: 'Arbeitsplan-Workflow aktualisiert.',
  },
  sections: {
    header: 'Arbeitsplankopf',
    translations: 'Sprachen',
    steps: 'Prozessschritte',
    history: 'Versionshistorie',
    documents: 'Arbeitsplan-DMS-Akte',
    snapshotDocuments: 'Dokument-Snapshot der Version',
    stepTranslations: 'Übersetzte Schrittanweisungen',
    byproducts: 'Nebenprodukte',
    stepDocuments: 'Schrittdokumente und Fotos',
  },
  fields: {
    number: 'Arbeitsplannummer',
    name: 'Bezeichnung',
    processFactor: 'Berechneter Gesamtprozessfaktor',
    description: 'Beschreibung',
    localizedName: 'Übersetzte Bezeichnung',
    position: 'Position',
    stepName: 'Schrittbezeichnung',
    workplace: 'Arbeitsplatz / Bereich',
    input: 'Eingang',
    output: 'Ausgang',
    yield: 'Ausbeute %',
    loss: 'Verlust %',
    version: 'Arbeitsplanversion',
    recipeVersion: 'Rezepturversion',
    status: 'Status',
    updated: 'Letzte Änderung',
    workCenter: 'Arbeitsplatz',
    productionArea: 'Produktionsbereich',
    inputQuantity: 'Eingangsmenge',
    inputUnit: 'Eingangseinheit',
    outputQuantity: 'Ausgangsmenge',
    outputUnit: 'Ausgangseinheit',
    targetTime: 'Sollzeit (min)',
    setupTime: 'Rüstzeit (min)',
    duration: 'Dauer (min)',
    temperature: 'Temperatur',
    temperatureUnit: 'Temperatureinheit',
    scrap: 'Ausschuss %',
    absorption: 'Aufnahme %',
    instructions: 'Arbeitsanweisung',
    parameters: 'Konfigurierbare Parameter (JSON)',
    qualityChecks: 'Qualitätsprüfungen (JSON)',
    product: 'Artikel',
    document: 'Dokument',
    photo: 'Foto',
    reason: 'Nachvollziehbare Begründung / Notiz',
  },
  states: { noDocuments: 'Keine geschützten Prozessdokumente vorhanden.' },
  stepDialog: {
    title: 'Prozessschritt bearbeiten',
    description:
      'Mengen, Zeiten, Parameter, Prüfungen, Verluste, Anweisungen, Nebenprodukte und Nachweise pflegen.',
  },
  actionDialog: {
    REVIEW: {
      title: 'Fachprüfung abschließen',
      description: 'Die unabhängige Prüfung mit einer nachvollziehbaren Notiz bestätigen.',
    },
    APPROVE: {
      title: 'Arbeitsplan freigeben',
      description:
        'Die Freigabe friert den Arbeitsplan ein und verlangt eine freigegebene Rezeptur sowie Vier-Augen-Trennung.',
    },
    COPY: {
      title: 'Neue Arbeitsplanversion erzeugen',
      description: 'Kopiert diesen Snapshot mit Quellverweis in einen neuen bearbeitbaren Entwurf.',
    },
    BLOCK: {
      title: 'Arbeitsplan sperren',
      description:
        'Die kontrollierte Version bleibt unveränderlich, darf aber nicht mehr verwendet werden.',
    },
    ARCHIVE: {
      title: 'Arbeitsplan archivieren',
      description:
        'Die Version bleibt vollständig nachvollziehbar und wird aus der aktiven Nutzung genommen.',
    },
  },
} as const;

export const batchScalingDe = {
  title: 'Batch Scaling',
  subtitle: 'Reproduzierbare serverseitige Materialberechnung in beiden Planungsrichtungen.',
  help: {
    open: 'Hilfe zum Batch Scaling öffnen',
    input:
      'Freigegebene Rezeptur, Richtung, Ausgabeeinheit und optional freigegebene Substitutionen wählen.',
    materials:
      'Bedarfe enthalten Komponentenverluste und alle Unterrezepturen und werden nach der Zieleinheit gerundet.',
    formulas:
      'Jedes Zwischenergebnis und die exakte serverseitig verwendete Formel werden in der Antwort ausgewiesen.',
  },
  sections: {
    input: 'Berechnungseingaben',
    materials: 'Materialbedarf',
    limiting: 'Limitierende Materialien',
    subRecipes: 'Aufgelöste Unterrezepturen',
    byproducts: 'Erwartete Nebenprodukte',
    formulas: 'Formeln und Zwischenergebnisse',
    substitutions: 'Freigegebene Substitutionen',
    available: 'Verfügbare Ausgangsmaterialien',
  },
  fields: {
    recipe: 'Rezepturversion',
    direction: 'Berechnungsrichtung',
    outputUnit: 'Ausgabeeinheit',
    position: 'Position {{position}}',
    primaryComponent: 'Hauptkomponente verwenden',
    targetQuantity: 'Gewünschte Endmenge',
    material: 'Material',
  },
  directions: {
    TARGET_OUTPUT: 'Gewünschte Endmenge → benötigte Materialien',
    AVAILABLE_INPUT: 'Vorhandenes Material → mögliche Endmenge',
  },
  actions: { addMaterial: 'Material hinzufügen', calculate: 'Batch berechnen' },
  result: {
    output: 'Mögliche / gewünschte Ausgabe',
    scale: 'Referenzskalierung',
    processFactor: 'Gesamtprozessfaktor',
    referenceOutput: 'Wirksame Referenzausgabe',
    level: 'BOM-Ebene {{level}}',
  },
  columns: {
    article: 'Artikel',
    type: 'Typ',
    quantity: 'Bedarfsmenge',
    source: 'Herkunftspfade',
    available: 'Verfügbar',
    requiredReference: 'Bedarf je Referenz',
    possibleOutput: 'Mögliche Ausgabe',
  },
  componentTypes: { MATERIAL: 'Material', PACKAGING: 'Verpackung' },
  states: { none: 'Keine.' },
  messages: { invalid: 'Die Berechnungseingaben sind ungültig.' },
  formulas: {
    PROCESS_STEP_FACTOR: 'Prozessschrittfaktor',
    OVERALL_PROCESS_FACTOR: 'Gesamtprozessfaktor',
    ROOT_RECIPE_SCALE: 'Skalierung der Hauptrezeptur',
    COMPONENT_GROSS_REQUIREMENT: 'Bruttobedarf der Komponente',
    SUB_RECIPE_SCALE: 'Skalierung der Unterrezeptur',
    AVAILABLE_MATERIAL_OUTPUT: 'Mögliche Ausgabe aus Material',
  },
  warnings: {
    PROCESS_ROUTING_MISSING_ASSUMED_100_PERCENT:
      'Kein Arbeitsplan vorhanden; 100 % Prozessausbeute werden angenommen und müssen geprüft werden.',
    PROCESS_ROUTING_NOT_APPROVED: 'Der gewählte Arbeitsplan ist nicht freigegeben.',
    STEP_YIELD_MISSING_ASSUMED_100_PERCENT:
      'Bei einem Schritt fehlen Ausbeute und Ein-/Ausgangsdaten; 100 % werden angenommen.',
    STEP_YIELD_CONTRADICTS_INPUT_OUTPUT:
      'Die gepflegte Ausbeute widerspricht dem Ein-/Ausgangsverhältnis des Schritts.',
    RECIPE_VERSION_NOT_APPROVED:
      'Mindestens eine Rezepturversion in der Hierarchie ist nicht freigegeben.',
    COMPONENT_PRODUCT_BLOCKED_OR_ARCHIVED: 'Ein benötigter Artikel ist gesperrt oder ausgelaufen.',
    MATERIAL_AVAILABILITY_NOT_PROVIDED:
      'Für dieses benötigte Material wurde keine Verfügbarkeit angegeben; es begrenzt das Ergebnis nicht.',
    AVAILABLE_MATERIAL_NOT_REQUIRED:
      'Ein angegebenes Material wird von der gewählten Struktur nicht benötigt.',
  },
} as const;
